"use client";

import type { ImageSwitcherBlock } from "@mw/shared/payload-types";
import type { Media } from "@mw/shared/payload-types";

import BackgroundForegroundImage from "@/app/_ui/components/core/BackgroundForegroundImage";
import { Image } from "@ui/components/core/Image";
import { DesktopMobileImage } from "@ui/components/core/DesktopMobileImage";
import { TabList } from "@ui/components/core/TabList";
import { ImageSizes } from "@ui/lib/image";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useState } from "react";
import { Tabs, type Key } from "react-aria-components";

import { Block } from "./Block";

export type SwitcherPosition = "bottom" | "top";

export const BlockImageSwitcher = ({ images, switcherPosition = "bottom" }: ImageSwitcherBlock) => {
  const [selectedKey, setSelectedKey] = useState<Key>("image-0");

  if (!images || images.length === 0) return null;

  const selectedIndex = images.findIndex((_, index) => `image-${index}` === selectedKey);
  const currentImage = images[selectedIndex];
  const imageConfig = currentImage?.asset.backgroundForegroundImage;

  if (!imageConfig?.foreground) return null;

  const tabItems = images.map((image, index) => ({
    icon: image?.switcherIcon ? <Image className='tw-h-4 tw-w-4' resource={image.switcherIcon as Media} /> : undefined,
    id: `image-${index}`,
    title: image?.label || `Image ${index + 1}`,
  }));

  if (switcherPosition === "top") {
    return (
      <BlockImageSwitcherTop
        imageConfig={imageConfig}
        images={images}
        selectedKey={selectedKey}
        setSelectedKey={setSelectedKey}
        tabItems={tabItems}
      />
    );
  }

  return (
    <BlockImageSwitcherBottom
      imageConfig={imageConfig}
      images={images}
      selectedKey={selectedKey}
      setSelectedKey={setSelectedKey}
      tabItems={tabItems}
    />
  );
};

BlockImageSwitcher.displayName = "BlockImageSwitcher";

// Shared props for variant components
interface VariantProps {
  imageConfig: NonNullable<ImageSwitcherBlock["images"]>[number]["asset"]["backgroundForegroundImage"];
  images: NonNullable<ImageSwitcherBlock["images"]>;
  selectedKey: Key;
  setSelectedKey: (key: Key) => void;
  tabItems: { icon?: React.ReactNode; id: string; title: string }[];
}

// Variant: Switcher at Bottom (original)
const BlockImageSwitcherBottom = ({ imageConfig, images, selectedKey, setSelectedKey, tabItems }: VariantProps) => {
  const selectedIndex = images.findIndex((_, index) => `image-${index}` === selectedKey);

  return (
    <Block name='image-switcher'>
      <div className='container px-16 py-16'>
        {/* Image Display */}
        <BackgroundForegroundImage
          animationKey={selectedIndex}
          aspectRatio='2x1'
          background={imageConfig?.background as Media}
          className='z-2 aspect-3x2 w-full lg:aspect-2x1'
          disableScaleAnimation
          foreground={imageConfig?.foreground as Media}
          imageSize={ImageSizes.FULL}
          minimumOpacity={0.8}
          mobileForeground={imageConfig?.mobileForeground as Media}
          overlay={imageConfig?.overlay as Media}
          rounded='xxl'
          theme='light'
        />
        <div className='relative mx-auto mb-20 flex h-48 max-w-xl px-32'>
          <div className='border-gray-100 h-48 flex-grow rounded-bl-xl border-b border-l' />
          {/* Slide Toggle */}
          {images.length > 1 && (
            <div className='relative flex translate-y-1/2 justify-center bg-eggshell'>
              <Tabs selectedKey={selectedKey} onSelectionChange={setSelectedKey}>
                <TabList items={tabItems} size='lg' />
              </Tabs>
            </div>
          )}
          <div className='border-gray-100 h-48 flex-grow rounded-br-xl border-b border-r' />
        </div>
      </div>
    </Block>
  );
};

// Variant: Switcher at Top
const BlockImageSwitcherTop = ({ images, selectedKey, setSelectedKey, tabItems }: VariantProps) => {
  const selectedIndex = images.findIndex((_, index) => `image-${index}` === selectedKey);
  const currentImageConfig = images[selectedIndex]?.asset.backgroundForegroundImage;
  const nextIndex = (selectedIndex + 1) % images.length;
  const nextImageConfig = images[nextIndex]?.asset.backgroundForegroundImage;

  return (
    <Block name='image-switcher-top' className='tw-w-full tw-flex tw-flex-col tw-items-center'>
      {images.length > 1 && (
        <div className='tw-container tw-mx-auto tw-mb-10 tw-flex tw-justify-center'>
          <Tabs selectedKey={selectedKey} onSelectionChange={setSelectedKey}>
            <TabList items={tabItems} size='lg' className='tw-m-1' />
          </Tabs>
        </div>
      )}

      <div className='tw-w-full md:tw-mx-auto md:tw-px-12 lg:tw-px-24 xl:tw-px-44'>
        <div className='tw-relative tw-aspect-[3/2] md:tw-aspect-[15/7] tw-overflow-hidden md:tw-rounded-t-2xl'>
          <AnimatePresence initial={false} mode='wait'>
            {currentImageConfig && (
              <m.div
                key={selectedIndex}
                className='tw-absolute tw-inset-0'
                initial={{ opacity: 0.05 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0.05 }}
                transition={{ duration: 0.25 }}
              >
                <DesktopMobileImage
                  imageConfig={currentImageConfig}
                  className='tw-object-cover'
                  mobileClassName='tw-object-left-top'
                />
              </m.div>
            )}
          </AnimatePresence>

          {/* Preload next image */}
          {nextImageConfig && nextIndex !== selectedIndex && (
            <div className='tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-0'>
              <DesktopMobileImage
                imageConfig={nextImageConfig}
                className='tw-object-cover'
                mobileClassName='tw-object-left-top'
              />
            </div>
          )}
        </div>
      </div>
    </Block>
  );
};
